/**
 * CommunitySubscribeCTA — the no-access placeholder for the community wall.
 *
 * Rendered when the current user is neither the wall owner nor an active
 * exclusive-content subscriber (`!hasAccess`). Shows the subscribe-to-access
 * blurb plus a "Subscribe" button that opens `SubscribeUserSheet` (wired by the
 * host page through `onSubscribePress`).
 *
 * Flutter source of truth: `subscribeToExclusiveContentWidget`
 * (`community_screen.dart:951-995`) — a centered blurb
 * (`_subscribeToExclusiveContentMessage`, `community_screen.dart:101-103`) plus
 * a bordered "Subscribe" button calling `_onSubscribeTapped`
 * (`community_screen.dart:891-901`).
 *
 * Reuses the common `SubscribeCTA` (the canonical web subscribe placeholder,
 * shared with the chat surface): this component only resolves the
 * community-scoped l10n strings (`community.subscribeToAccess` with the `{name}`
 * substitution, `common.subscribe`) and wraps the CTA in a community-scoped qa
 * root so the qa-tester can scope to the placeholder. The inner Subscribe button
 * carries the community-scoped `community-subscribe-button` qa id (threaded
 * through the shared CTA's `buttonQaId` prop) so the qa-tester can locate it.
 */

import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { SubscribeCTA } from '@/presentation/components/common/SubscribeCTA';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface CommunitySubscribeCTAProps {
  /**
   * Display name of the wall owner — substituted into the subscribe-to-access
   * blurb (`community.subscribeToAccess` `{name}`). Mirrors Flutter's
   * `widget.user.name` interpolation (`community_screen.dart:101-103`).
   */
  creatorName: string;
  /**
   * Invoked when the Subscribe button is tapped. The host page wires this to
   * `SubscribeUserSheet.show` (Flutter `_onSubscribeTapped`,
   * `community_screen.dart:891-901`).
   */
  onSubscribePress: () => void;
}

export const CommunitySubscribeCTA: React.FC<CommunitySubscribeCTAProps> = ({
  creatorName,
  onSubscribePress,
}) => {
  const { l10n } = useLocale();

  return (
    <div
      {...qaAttr({ id: 'community-subscribe-cta' })}
      // The host page mounts this in a `display: flex` band that carries
      // `alignItems: 'center'` + `justifyContent: 'center'`, so this content-sized
      // root is centered on both axes by the wrapper itself (mirroring the sibling
      // `showBlocked` branch) rather than via a `height: '100%'` percentage chain.
      style={{ width: '100%' }}
    >
      <SubscribeCTA
        blurb={l10n.community.subscribeToAccess.replace('{name}', creatorName)}
        buttonText={l10n.common.subscribe}
        onSubscribePress={onSubscribePress}
        buttonQaId="community-subscribe-button"
      />
    </div>
  );
};
